import styles from './debug-output.module.scss';
import { Instance, ITopology, useLazyGetDebugStreamQuery } from "../../app/apiSlice";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { DataNodeModel } from '../../pages/editor/diagram/nodes/DataNodeModel';
import { InstanceStatus } from '../../pages/editor/logs/LogsDatas';
import { TabMenu } from 'primereact/tabmenu';
import { Logs } from '../../pages/editor/logs/Logs';
import { IconMessage } from '../IconMessage/IconMessage';
import { useAppSelector } from '../../hooks/hooks';

export enum WSStatus {
  CONNECTING,
  OPEN,
  CLOSING,
  CLOSED
}

export interface DebugOutputApi {
  open: (node: DataNodeModel) => void;
  close: () => void;
}

export interface DebugOutputProps {
  topology: ITopology;
}

export const DebugOutput = forwardRef((props: DebugOutputProps, ref) => {

  const LOG_PREFIX = '>>> WS - ';

  const {topologyData} = useAppSelector((state) => state.editor);
  const [getStream, {data: rawStreamData, error}] = useLazyGetDebugStreamQuery();
  const timerStreamRef = useRef<NodeJS.Timer>();
  const [ws, setWs] = useState<WebSocket>();
  const [node, setNode] = useState<DataNodeModel>();
  const [messages, setMessages] = useState<string[]>([]);
  const [wsStatus, setWsStatus] = useState<number>(-1);
  const [activeTab, setActiveTab] = useState(0);
  const [instanceStatus, setInstanceStatus] = useState<InstanceStatus>();

  const PT_TABMENU = {
    root: {style: {flexShrink: '0'}},
    action: {style: {padding: '.7rem 1rem'}}
  };

  // ---------------------------------------------------------------------------------------------------------
  // PUBLIC METHODS
  // ---------------------------------------------------------------------------------------------------------

  useImperativeHandle(ref, (): DebugOutputApi => ({

    open(node: DataNodeModel) {
      const newUrl = buildWebsocketUrl(props.topology.id, node.getID());
      const currentUrl = ws ? ws.url : undefined;
      if (newUrl && newUrl !== currentUrl) {
        // Manage WS
        closeWS();
        setNode(node);
        openWS(newUrl);
        // Manage status
        setInstanceStatus(undefined);
        if (timerStreamRef.current) {
          clearInterval(timerStreamRef.current);
        }
        getStream({topologyid: props.topology.id, env: props.topology.environnement});
        timerStreamRef.current = setInterval(() => {
            getStream({topologyid: props.topology.id, env : props.topology.environnement});
        }, 3_000);
      }
    },

    close() {
      // Close WS
      closeWS();
      // Stop timer for status
      if (timerStreamRef.current) {
        clearInterval(timerStreamRef.current);
      }
    }

  }));

  // ---------------------------------------------------------------------------------------------------------
  // HOOKS
  // ---------------------------------------------------------------------------------------------------------

  useEffect(() => {
    const timer = setInterval(() => {
      const newStatus = ws ? ws.readyState : -1;
      if (newStatus !== wsStatus) {
        setWsStatus(newStatus);
      }
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [ws]);

  useEffect(() => {
    if (rawStreamData && rawStreamData.pods) {
      const pod = rawStreamData.pods.find((p) => p.name === node?.getID());
      if (pod && pod.instances && pod.instances.length > 0) {
        const instance: Instance = pod.instances[0];
        setInstanceStatus({
          status: instance.status,
          data: instance
        });
      } else {
        console.warn(`Pod with id ${node?.getID()} not found in stream data, or no instances for this pod`)
      }
    }
  }, [rawStreamData]);

  // ---------------------------------------------------------------------------------------------------------
  // CLASS NAMES
  // ---------------------------------------------------------------------------------------------------------

  const CN_WS_Status = () => {
    switch(wsStatus) {
      case WSStatus.CONNECTING:
        return styles['ws-status-connecting'];
      case WSStatus.OPEN:
        return styles['ws-status-open'];
      case WSStatus.CLOSING:
        return styles['ws-status-closing'];
      case WSStatus.CLOSED:
        return styles['ws-status-closed'];
      default:
        return styles['ws-status-undefined'];
    }
  }

  // ---------------------------------------------------------------------------------------------------------
  // PRIVATE METHODS
  // ---------------------------------------------------------------------------------------------------------

  const debug = (url: string, msg: string) => {
    const boxId = url.split('/').pop();
    console.debug(`${LOG_PREFIX}[${boxId}] ${msg}`)
  }

  const closeWS = () => {
    if (ws) {
      debug(ws.url, "Closing...");
      ws.close();
    }
  }

  const openWS = (wsUrl: string) => {

    setMessages([]);

    debug(wsUrl, "Opening...");

    const newWs = new WebSocket(wsUrl);

    newWs.onopen = () => {
      debug(newWs.url, "OPENED !");
    };
    newWs.onclose = () => {
      debug(newWs.url, "CLOSED !");
    };
    newWs.onmessage = (event) => {
      debug(newWs.url, "Message received !");
      setMessages((prevMessages) => [...prevMessages, event.data]);
    };
    setWs(newWs);
  }

  const buildWebsocketUrl = (topologyId: string, nodeId: string): string | undefined => {
    if (topologyId !== undefined && nodeId !== undefined) {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const isDev = process.env.REACT_APP_DEV === 'true';
      if (isDev) {
        return `ws://localhost:8081/ws/streams/${topologyId}/debug/${nodeId}`;
      } else {
        return `${wsProtocol}://${window.location.host}/ws/streams/${topologyId}/debug/${nodeId}`;
      }
    }
    console.log("got undefined in buildWebsocketUrl")
    return undefined;
  };

  // Return the schema of the previous node
  const getFields = (node: DataNodeModel|undefined): any => {
    if (node && topologyData) {
      const brickData: any = topologyData.find((n: any) => n.id === node.getID());
      try {
        const schema = JSON.parse(brickData?.schema);
        if (schema) {
          return schema.fields;
        }
      } catch (error) {
        console.error('Error while getting schema for node : ', node.getID());
      }
    }
    return [];
  }

  // ---------------------------------------------------------------------------------------------------------
  // PRIVATE HTML
  // ---------------------------------------------------------------------------------------------------------

  const buildTableHeader = () => {
    const fields = getFields(node);
    return (
      <thead>
        <tr>
          {fields && fields.map((field: any, index: number) => (
            <th key={index}>
              {field.name}
            </th>
          ))}
        </tr>
      </thead>
    );
  };

  const buildTableRow = (msg: string, index: number) => {
    const fields = getFields(node);
    let data: any = undefined;
    try {
      data = JSON.parse(msg);
    } catch (error) {
      console.error('Error received in request : ', msg);
    }
    return data && (
      <tr key={index}>
        {fields && fields.map((field: any, index: number) => (
          <td key={index}>
            {data[field.name]}
          </td>
        ))}
      </tr>
    );
  }

  const Header = () => {
    return (
      <div style={{display: 'flex', alignItems: 'center', marginBottom: '1rem', gap: '6px'}}>
        <span>Etat de la connection : </span>
        <span className={CN_WS_Status()}></span>
      </div>
    );
  }

  const Table = () => {
    return (
      <div className={styles['output-table-container']}>
        <table>
          {buildTableHeader()}
          <tbody>
            {messages && messages.map((msg: string, index: number) => {
              return buildTableRow(msg, index);
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // ---------------------------------------------------------------------------------------------------------
  // MAIN RENDERER
  // ---------------------------------------------------------------------------------------------------------

  return (
    <div className={styles['debug-output']}>
      <TabMenu model={[{label: 'Données de sortie'}, {label: 'Journal des logs'}]} activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)} pt={PT_TABMENU}/>
      <div className={styles['tabs-container']}>
        <div className={styles['tab-panel']} style={{display: activeTab === 0 ? 'flex' : 'none', flexDirection: 'column'}}>
          <Header/>
          <Table/>
        </div>
        <div className={styles['tab-panel']} style={{display: activeTab === 1 ? 'block' : 'none'}}>
          {instanceStatus && <Logs instanceStatus={instanceStatus}/>}
          {!instanceStatus &&
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '2rem'}}>
              <IconMessage icon={'fa-solid fa-spinner fa-spin-pulse'}>
                <div style={{lineHeight: '1.2rem'}}>Récupération des logs</div>
                <div style={{lineHeight: '1.2rem'}}>En attente de réponse...</div>
              </IconMessage>
            </div>
          }
        </div>
      </div>
    </div>
  );

});
